import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RouterModule, RouterOutlet } from '@angular/router';
import {
  EnvironmentService,
  SiaCacheDatabaseService,
  SiaI18nService,
  SiaMenuService,
  SiaNewRouterService,
  SiaParamsAndPatternsService,
  SiaPwaService,
  SiaSessionService,
  SiaSignalrService,
  SiaSizeDetectorService,
  SiaTokenStoreService,
  SiaTranslatePipe,
  SiaUsersService,
  ThemeService,
  removeLocalStorageItems,
} from '@sia/shared';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { AuthBgBlobsComponent } from './components/auth-bg-blobs/auth-bg-blobs.component';
import { SiaAuthResult } from './interfaces/sia-auth-result';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  standalone: true,
  imports: [
    MatButton,
    RouterOutlet,
    RouterModule,
    MatIcon,
    SiaTranslatePipe,
    AuthBgBlobsComponent,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthComponent implements OnDestroy {
  protected paramsAndPatternsService = inject(SiaParamsAndPatternsService);
  protected siaSizeDetectorService = inject(SiaSizeDetectorService);
  protected cacheDatabaseService = inject(SiaCacheDatabaseService);
  protected environmentService = inject(EnvironmentService);
  protected deviceService = inject(DeviceDetectorService);
  protected routerService = inject(SiaNewRouterService);
  protected signalrService = inject(SiaSignalrService);
  protected sessionService = inject(SiaSessionService);
  protected tokenStore = inject(SiaTokenStoreService);
  protected usersService = inject(SiaUsersService);
  protected i18nService = inject(SiaI18nService);
  protected menuService = inject(SiaMenuService);
  protected pwaService = inject(SiaPwaService);
  protected authService = inject(AuthService);
  protected themeService = inject(ThemeService);

  contentReady = signal<'loading' | 'input' | 'auto' | undefined>(undefined);
  loginStarted = signal(false);
  otp = signal('');

  #serverVersionRetriveInterval?: NodeJS.Timeout;
  install = signal(true);
  loginImage = signal('assets/auth/login.png');
  versionRetrieved = signal(false);
  dataLoaded = signal({
    serverVersion: false,
    clientVersion: false,
  });
  clientVersion = signal('');
  serverVersion = signal(' ');
  protected subs: Subscription[] = [];
  promptEvent = signal<any>(null);

  toUpdate = toSignal(this.pwaService.toUpdate$, { initialValue: true });

  pwaUpdated = computed(() => {
    if (this.environmentService.production) {
      return this.clientVersion() === this.serverVersion();
    }
    return true;
  });

  // Countdown logic moved here
  countdown = signal(180);
  buttonDisable = signal(true);
  minutes = computed(() => Math.floor(this.countdown() / 60));
  seconds = computed(() => this.countdown() % 60);
  seconds2Digits = computed(() => this.seconds().toString().padStart(2, '0'));
  #countdownInterval: any;

  constructor() {
    this.loginImage.set(
      window.localStorage.getItem('login-image') || 'assets/auth/login.png',
    );
    let s01 = this.pwaService.install$.subscribe((deferredPrompt) => {
      if (deferredPrompt) {
        this.install.set(this.isRunningStandalone());
        this.promptEvent.set(deferredPrompt);
      }
    });
    this.subs.push(s01);

    this.#manageVersions();
  }

  async #manageVersions() {
    let s02 = this.pwaService.clientVersion$.subscribe((ver) => {
      this.clientVersion.set(ver);
      this.dataLoaded.update((d) => ({ ...d, clientVersion: true }));
    });
    this.subs.push(s02);
    await this.#retriveServerVersion();
    this.#serverVersionRetriveInterval = setInterval(async () => {
      await this.#retriveServerVersion();
    }, 5000);
  }

  async #retriveServerVersion() {
    try {
      var serverVersion = await this.pwaService.getServerVersion();
      this.versionRetrieved.set(true);
      this.serverVersion.set(serverVersion);
    } catch (error) {
      console.error(error);
    } finally {
      this.dataLoaded.update((d) => ({ ...d, serverVersion: true }));
    }
  }

  update() {
    this.paramsAndPatternsService.resetDepartment();
    window.location.href = window.location.href;
    window.location.reload();
  }

  async installApp() {
    if (!this.promptEvent()) {
      return;
    }
    this.install.set(false);
    this.promptEvent().prompt();
    try {
      const choiceResult: any = await this.promptEvent().userChoice;
      if (choiceResult.outcome === 'accepted') {
      }
      this.promptEvent.set(null);
    } catch (e: any) {}
  }

  public shouldInstall(): boolean {
    return !this.isRunningStandalone() && this.promptEvent();
  }

  public isRunningStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  async initializeTranslations() {
    return await this.i18nService.updateSessionTranslations();
  }

  async loginAfterTokenReceived(result: SiaAuthResult, menuId?: string) {
    if (result.success === true) {
      await this.authService.refreshToken();
      var currentUser = this.usersService.getUserData();
      var deviceInfo = this.deviceService.getDeviceInfo();

      if (currentUser?.id) {
        await this.signalrService.reconnect(currentUser.id);
      }

      await this.initializeTranslations();
      try {
        await this.menuService.getUserMenus();
        if (currentUser) {
          this.signalrService.emitLoggedUser(currentUser);
        }
        let redirect: string | undefined = '';
        if (
          deviceInfo.deviceType &&
          deviceInfo.deviceType.toLowerCase() == 'mobile'
        ) {
          redirect = currentUser?.redirectMobileMenuId;
        } else {
          redirect = currentUser?.redirectMasterMenuId;
        }
        redirect = menuId || redirect || undefined;
        if (!redirect || !(await this.menuService.getMenu(redirect))) {
          await this.routerService.navigateMenuWithParams('default-login');
        } else {
          await this.routerService.navigateMenuWithParams(redirect);
        }
        await this.routerService.loadDrawerPerspective();
      } catch (e: any) {
        console.error('Errore durante il login', e);
        this.contentReady.set('input');
        const done = await this.authService.logout();
        if (done) {
          await this.performDisconnect();
        }
        throw e;
      }
    } else if (result.success === false) {
    }
  }

  async performDisconnect() {
    this.cacheDatabaseService.clearAllUserMenu();
    await this.routerService.logout();
  }

  protected async tryTokenLogin() {
    const isExpired = await this.tokenStore.isCurrentTokenExpired();
    const hasToken = !!this.tokenStore.getAccessToken();

    if (hasToken && !isExpired) {
      this.contentReady.set('auto');
      const token = this.tokenStore.getToken();
      await this.loginAfterTokenReceived({ success: true, token } as any);
      return;
    }

    const refreshToken = this.tokenStore.getRefreshToken();
    if (refreshToken) {
      this.contentReady.set('auto');
      try {
        const result = await this.authService.getStoredToken();
        if (result?.token) {
          await this.loginAfterTokenReceived(result);
        } else {
          await this.performDisconnect(); // Clear state if refresh fails
          this.contentReady.set('input');
        }
      } catch (e) {
        await this.performDisconnect();
        this.contentReady.set('input');
      }
    } else {
      this.tokenStore.clearStore();
      this.contentReady.set('input');
    }
  }

  async clearSession() {
    const isTokenExpired = await this.tokenStore.isCurrentTokenExpired();
    if (isTokenExpired) {
      await this.cacheDatabaseService.clearDatabase();
      removeLocalStorageItems();
      var urls = window.sessionStorage.getItem('APIURLS');
      const keys = Object.keys(window.sessionStorage);
      this.sessionService.clearStorage();
      keys.forEach((key) => {
        if ('apiurls'.indexOf(key.toLowerCase()) == -1) {
          sessionStorage.removeItem(key);
        }
      });
      if (urls) {
        window.sessionStorage.setItem('APIURLS', urls);
      }
    }
  }

  async forceClearSession() {
    await this.cacheDatabaseService.clearDatabase();
    this.tokenStore.clearStore();
    removeLocalStorageItems();
    var urls = window.sessionStorage.getItem('APIURLS');
    const keys = Object.keys(window.sessionStorage);
    this.sessionService.clearStorage();
    keys.forEach((key) => {
      if ('apiurls'.indexOf(key.toLowerCase()) == -1) {
        sessionStorage.removeItem(key);
      }
    });
    if (urls) {
      window.sessionStorage.setItem('APIURLS', urls);
    }
  }

  protected isOtpInvalid = computed((): boolean => {
    return !this.otp() || this.otp().length != 6 || !/^\d+$/.test(this.otp());
  });

  startCountDown(seconds: number = 180) {
    this.stopCountDown();
    this.countdown.set(seconds);
    this.buttonDisable.set(true);

    this.#countdownInterval = setInterval(() => {
      this.countdown.update((c) => c - 1);
      if (this.countdown() <= 0) {
        this.stopCountDown();
        this.buttonDisable.set(false);
      }
    }, 1000);
  }

  stopCountDown() {
    if (this.#countdownInterval) {
      clearInterval(this.#countdownInterval);
      this.#countdownInterval = null;
    }
  }

  resetCountdown() {
    this.stopCountDown();
    this.countdown.set(180);
    this.buttonDisable.set(true);
  }

  ngOnInit() {
    const apiUrls = window.sessionStorage.getItem('APIURLS');
    if (!apiUrls) {
      console.error('APIURLS not found in sessionStorage');
      window.location.reload();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
    clearInterval(this.#serverVersionRetriveInterval);
    this.stopCountDown();
  }
}
